import { useEffect } from "react";
import { Button, Col, Modal, Row, message } from "antd";
import { CustomInput } from "components/Elements";
import { Controller, useForm } from "react-hook-form";
import {
  addCustomer,
  updateCustomer,
} from "store/features/customer/customer.service";
import { useAppDispatch } from "store/store";
import { AddCustomerI } from "types";
import { yupResolver } from "@hookform/resolvers/yup";
import { createCustomerSchema } from "validations";
import { GTagEventCategory, GTagEventType, sendGTagEvent } from "utils/gtag";
import { formatCNIC } from "utils/text";

const defaultCustomerData: AddCustomerI = {
  name: "",
  phone: "",
  cnic: "",
  amountGiven: 0,
  amountReceived: 0,
  email: "",
  address: "",
  details: "",
};

type AddCustomerModalI = {
  open: boolean;
  type: "edit" | "add";
  defaultValues?: (AddCustomerI & { id: string }) | null;
  onCancel: () => void;
};

const AddCustomerModal = ({
  open,
  type,
  defaultValues,
  onCancel,
}: AddCustomerModalI) => {
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createCustomerSchema),
  });

  const onSubmit = (data: AddCustomerI) => {
    if (type === "add") {
      dispatch(addCustomer({ data, type: "customer" }))
        .unwrap()
        .then(() => {
          // sending gTag event here...
          sendGTagEvent(
            GTagEventType.ADD_CUSTOMER,
            GTagEventCategory.CUSTOMERS
          );

          reset();
          message.success("Customer has been created successfully!");
          onCancel();
        })
        .catch((errMessage: string) => {
          message.error(errMessage);
        });
    }

    if (type === "edit") {
      dispatch(
        updateCustomer({
          id: defaultValues?.id,
          data,
        })
      )
        .unwrap()
        .then(() => {
          // sending gTag event here...
          sendGTagEvent(
            GTagEventType.UPDATE_CUSTOMER,
            GTagEventCategory.CUSTOMERS
          );

          reset();
          message.success("Customer has been updated successfully!");
          onCancel();
        })
        .catch((errMessage: string) => {
          message.error(errMessage);
        });
    }
  };

  // @ts-ignore
  const addCustomerHandler = () => handleSubmit(onSubmit)();

  useEffect(() => {
    if (type === "add") {
      reset(defaultCustomerData);
    } else if (type === "edit" && defaultValues) {
      reset({
        address: defaultValues.address,
        amountGiven: defaultValues.amountGiven,
        amountReceived: defaultValues.amountReceived,
        details: defaultValues.details,
        email: defaultValues.email,
        name: defaultValues.name,
        phone: defaultValues.phone,
        cnic: defaultValues.cnic,
      });
    }
  }, [type, defaultValues]);

  return (
    <Modal
      title={`${type === "add" ? "Add" : "Edit"} Customer`}
      open={open}
      onOk={onCancel}
      onCancel={onCancel}
      width={668}
      footer={[
        <Button
          size="large"
          type="primary"
          style={{ marginTop: "1rem" }}
          onClick={addCustomerHandler}
          htmlType="submit"
        >
          {type === "add" ? "Add" : "Update"}
        </Button>,
      ]}
    >
      <div className="modal">
        <Row gutter={[28, 28]}>
          <Col xs={24} md={12}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <>
                  <CustomInput
                    label="Name*"
                    placeholder="Enter your name"
                    {...field}
                  />
                  {errors.name && (
                    <p className="fieldValidationError">
                      {errors.name.message}
                    </p>
                  )}
                </>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <>
                  <CustomInput
                    label="Phone Number*"
                    placeholder="+923XXXXXXXXX"
                    maxLength={13}
                    {...field}
                  />
                  {errors.phone && (
                    <p className="fieldValidationError">
                      {errors.phone.message}
                    </p>
                  )}
                </>
              )}
            />
          </Col>
        </Row>
        <Row gutter={[28, 28]}>
          <Col xs={24} md={12}>
            <>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <CustomInput
                    label="Email Address"
                    placeholder="Enter email address"
                    {...field}
                  />
                )}
              />
              {errors.email && (
                <p className="fieldValidationError">{errors.email.message}</p>
              )}
            </>
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="cnic"
              control={control}
              render={({ field }) => (
                <>
                  <CustomInput
                    label="CNIC"
                    placeholder="XXXXX-XXXXXXX-X"
                    {...field}
                    onChange={(e) => field.onChange(formatCNIC(e.target.value))}
                  />
                  {errors.cnic && (
                    <p className="fieldValidationError">
                      {errors.cnic.message}
                    </p>
                  )}
                </>
              )}
            />
          </Col>
        </Row>
        <Row gutter={[28, 28]}>
          <Col xs={24} md={12}>
            <Controller
              name="amountGiven"
              control={control}
              render={({ field }) => (
                <>
                  <CustomInput
                    {...field}
                    type="number"
                    label="Amount Given"
                    placeholder="Amount"
                    value={String(field.value)}
                    onChange={(e) => {
                      const numericValue = parseInt(e.target.value);
                      field.onChange(isNaN(numericValue) ? 0 : numericValue);
                    }}
                  />
                  {errors.amountGiven && (
                    <p className="fieldValidationError">
                      {errors.amountGiven.message}
                    </p>
                  )}
                </>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="amountReceived"
              control={control}
              render={({ field }) => (
                <>
                  <CustomInput
                    {...field}
                    type="number"
                    label="Amount Received"
                    placeholder="Amount"
                    value={String(field.value)}
                    onChange={(e) => {
                      const numericValue = parseInt(e.target.value);
                      field.onChange(isNaN(numericValue) ? 0 : numericValue);
                    }}
                  />
                  {errors.amountReceived && (
                    <p className="fieldValidationError">
                      {errors.amountReceived.message}
                    </p>
                  )}
                </>
              )}
            />
          </Col>
        </Row>
        <Row gutter={[28, 28]}>
          <Col xs={24} md={12}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <>
                  <CustomInput
                    label="Location*"
                    placeholder="Enter customer location"
                    {...field}
                  />
                  {errors.address && (
                    <p className="fieldValidationError">
                      {errors.address.message}
                    </p>
                  )}
                </>
              )}
            />
          </Col>

          <Col xs={24} md={12}>
            <Controller
              name="details"
              control={control}
              render={({ field }) => (
                <>
                  <CustomInput
                    label="Details (optional)"
                    placeholder="Further Info"
                    {...field}
                  />
                  {errors.details && (
                    <p className="fieldValidationError">
                      {errors.details.message}
                    </p>
                  )}
                </>
              )}
            />
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export { AddCustomerModal };
